<template>
    <form @submit.prevent="submit" @input="clear" class="rounded-xl bg-light-gray px-7 pb-7 pt-4 shadow-lg">
        <div class="space-y-4">
            <FormInput
                id="name"
                type="text"
                label="Your name"
                :error="errors.name"
                maxlength="200"
                v-model="name"
                required
            />

            <FormInput
                id="email"
                type="email"
                label="Company email"
                :error="errors.email"
                v-model="email"
                placeholder="you@goodcompany.com"
                required
            />

            <div class="field">
                <label for="employee_number"
                    >How many employees will we help you celebrate?<sup class="font-semibold text-red-500"
                        >*</sup
                    ></label
                >
                <div class="control">
                    <Multiselect
                        id="employee_number"
                        label="label"
                        :searchable="false"
                        :options="employeeNumOptions"
                        :show-labels="false"
                        :allow-empty="false"
                        v-model="employeeNumber"
                        class="smaller-tag"
                    />
                </div>
            </div>

            <FormInput
                id="message"
                type="textarea"
                rows="4"
                label="Any extra context that will help?"
                :error="errors.message"
                v-model="message"
                placeholder="e.g. How do you plan to use Thankbox? How do we best set you up for success? (Optional)"
            />
        </div>

        <p class="my-3 text-xs text-dark-gray">
            We will use the information you provide to contact you about our relevant products. You can unsubscribe at
            any time.
        </p>

        <button
            v-if="!submissionSuccess"
            id="business-contact-form-submit-button"
            class="button image w-full"
            type="submit"
            :disabled="submitting"
            :class="{ loading: submitting }"
        >
            <i class="fa-solid fa-paper-plane"></i>
            <span>Send enquiry</span>
        </button>

        <p class="text-red mt-3 text-sm" v-if="error">
            {{ error }}
        </p>

        <p class="mt-3 text-sm text-green" v-if="submissionSuccess">
            We've received your enquiry and will be in touch soon. Congratulations on taking the first step towards a
            more joyful workplace!
        </p>
    </form>
</template>

<script setup lang="ts">
import { ref } from 'vue'
import Multiselect from 'vue-multiselect'
import axios from '@/util/axios'
import isEmpty from '@/util/isEmpty'
import FormInput from '@/components/FormInput.vue'

interface EmployeeNumber {
    label: string
    value: string
}

const employeeNumOptions: EmployeeNumber[] = [
    { label: 'Less than 50', value: '1-49' },
    { label: '50-100', value: '50-100' },
    { label: '101-500', value: '101-500' },
    { label: '501-1000', value: '501-1000' },
    { label: '1001-5000', value: '1001-5000' },
    { label: '5001-10000', value: '5001-10000' },
    { label: 'More than 10000', value: '10000+' },
]

const name = ref<string>('')
const email = ref<string>('')
const employeeNumber = ref<EmployeeNumber | null>(null)
const message = ref<string>('')
const submitting = ref(false)
const submissionSuccess = ref(false)

const errors = ref<any>({})
const error = ref<string>('')

const clear = () => {
    errors.value = {}
}

const submit = async () => {
    if (submitting.value) return

    submitting.value = true

    axios
        .post('/business-contact-form-submission', {
            name: name.value,
            email: email.value,
            employee_count: employeeNumber.value?.value,
            message: message.value,
        })
        .then(() => {
            submissionSuccess.value = true
        })
        .catch(error => {
            errors.value = error.response?.data?.errors || {}
            if (isEmpty(errors.value)) {
                error.value = 'An error occurred when submitting the form. Please try again.'
            }
        })
        .finally(() => {
            submitting.value = false
        })
}
</script>
